/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useCallback} from "react";
import {Box, Card, Tab, Tabs, Typography, useTheme} from "@mui/material";
import {BoxplotChart, BubbleMapChart, ChoroplethChart, FunnelChart, LinearParallelCoordinatesPlotChart, MatrixChart, SankeyChart, ViolinChart} from "../../components/custom-chartjs-charts";
import {ChartData, ChartDataset, ChartOptions} from "chart.js";
import Grid2 from "@mui/material/Unstable_Grid2";
// noinspection ES6UnusedImports
import type * as FunnelTypes from 'chartjs-chart-funnel';
import chroma from "chroma-js";
import CanadaTopoJson from 'datamaps/src/js/data/can.topo.json';
import USATopoJson from 'datamaps/src/js/data/usa.topo.json';
import MexicoTopoJson from 'datamaps/src/js/data/mex.topo.json';
import {topojson} from "chartjs-chart-geo";
import {GeometryCollection, Topology} from "topojson-specification";
import * as GeoJSON from "geojson";

const TestView: React.FC = () => {

    return (
        <>
            <Box sx={{backgroundColor: 'background.default', padding: 3}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
                    <Typography variant={'h5'} fontWeight={600}>Chart.js Extended Charts Demo</Typography>
                </Box>
                <DemoCharts/>
            </Box>
        </>
    );
}

export default TestView;


const DemoCharts: React.FC = () => {

    return (
        <>
            <Grid2 container spacing={3} mb={3} sx={{width: '100%'}}>
                <Grid2 xs={12}>
                    <ChartInfo title={"Funnel Chart"}>
                        <DemoFunnelChart/>
                    </ChartInfo>
                </Grid2>
                <Grid2 xs={12}>
                    <ChartInfo title={"Boxplot Chart"}>
                        <DemoBoxplotChart/>
                    </ChartInfo>
                </Grid2>
                <Grid2 xs={12}>
                    <ChartInfo title={"Violin Chart"}>
                        <DemoViolinChart/>
                    </ChartInfo>
                </Grid2>
                <Grid2 xs={12}>
                    <ChartInfo title={"Choropleth Map Chart"} height={800}>
                        <DemoChoroplethMapChart/>
                    </ChartInfo>
                </Grid2>
                <Grid2 xs={12}>
                    <ChartInfo title={"Bubble Map Chart"} height={800}>
                        <DemoBubbleMapChart/>
                    </ChartInfo>
                </Grid2>
                <Grid2 xs={12}>
                    <ChartInfo title={"Matrix Chart"}>
                        <DemoMatrixChart/>
                    </ChartInfo>
                </Grid2>
                <Grid2 xs={12}>
                    <ChartInfo title={"PCP Chart"} height={800}>
                        <DemoPCPChart/>
                    </ChartInfo>
                </Grid2>
                <Grid2 xs={12}>
                    <ChartInfo title={"Sankey Chart"}>
                        <DemoSankeyChart/>
                    </ChartInfo>
                </Grid2>
                <Grid2 xs={12}>
                    <ChartInfo title={"Venn Diagram Chart"}>
                        <DemoVennDiagramChart/>
                    </ChartInfo>
                </Grid2>
            </Grid2>
        </>
    );
}

interface ChartInfoProps {
    title: string;
    height?: number;
}

interface ChartInfoChartProps extends Omit<React.HTMLProps<HTMLCanvasElement>, 'data' | 'ref'> {
}

const ChartInfo: React.FC<React.PropsWithChildren<ChartInfoProps>> = ({title, children, height}) => {

    const chart = React.useMemo(() => {
        const chart = React.Children.only(children);
        if (!React.isValidElement(chart)) {
            return null;
        }
        return React.cloneElement(chart, {
            height: '100%',
            width: '100%',
            style: {height: '100%', width: '100%'},
        });
    }, [children])

    if (!chart) {
        return null;
    }

    return (
        <>
            <Card variant={'outlined'} sx={{border: 'none', padding: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                <Typography fontWeight={600} variant={'subtitle1'}>{title}</Typography>
                <Box sx={{minHeight: height ?? 400, maxHeight: (height ?? 400) + 50}}>
                    {chart}
                </Box>
            </Card>
        </>
    );
}

const DemoFunnelChart: React.FC<ChartInfoChartProps> = (props) => {
    const theme = useTheme();

    const chartData = React.useMemo<ChartData<'funnel'>>(() => ({
        labels: [
            {
                label: 'Step 1',
                children: [
                    'Step 1.1',
                    'Step 1.2',
                ],
            },
            'Step 2',
            'Step 3',
            'Step 4',
        ],
        datasets: [
            {
                tree: [
                    {value: 0.9, children: [0.85, 0.8]},
                    0.7,
                    0.5,
                    0.1,
                ],
                label: "Demo Funnel Dataset 2",
                backgroundColor: chroma.scale('Oranges').mode('lab').colors(6),
                hoverBackgroundColor: chroma.scale('PuBu').mode('lab').colors(6),
                borderColor: chroma.scale('Oranges').mode('lab').colors(6),
                borderWidth: 2,
            } as unknown as ChartDataset<"funnel", number[]>,
        ],

    }), []);


    const options = React.useMemo<ChartOptions<'funnel'>>(() => ({
        onClick: (event, elements, chart) => {
            console.log('Funnel chart clicked', {event, elements, chart});
        },
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        borderColor: theme.palette.divider,
        plugins: {
            legend: {
                display: true,
            },
            datalabels: {
                display: true,
                anchor: (context) => {
                    const value = context.dataset.data[context.dataIndex] as number;
                    return value < 0.02 ? 'end' : 'start';
                },
                align: (context) => {
                    const value = context.dataset.data[context.dataIndex] as number;
                    return value < 0.02 ? 'end' : 'center';
                },
                textAlign: 'center',
                font: {size: 14},
                formatter: (value, context) => {
                    const label = context.chart.data.labels?.[context.dataIndex];
                    return `${label ?? ""}\n${(value * 100).toLocaleString()}%`;
                }
            },
            tooltip: {
                enabled: true,
            }
        },
        layout: {
            padding: {
                left: 40,
            },
        },
        scales: {
            y: {
                display: true,
                type: 'hierarchical',
                padding: 25,
                levelPercentage: 1,
                grid: {
                    display: true,
                    color: theme.palette.divider,
                }
            },
            x: {
                grid: {
                    display: true,
                    color: theme.palette.divider,
                }
            },
        }
    }), [theme]);

    return (
        <>
            <FunnelChart
                {...props}
                data={chartData}
                options={options}
            />
        </>
    );
}


const DemoBoxplotChart: React.FC<ChartInfoChartProps> = (props) => {
    const theme = useTheme();

    function randomValues(count: number, min: number, max: number, extra: number[] = []): number[] {
        const delta = max - min;
        return [...Array.from({length: count}).map(() => Math.random() * delta + min), ...extra];
    }

    const chartData = React.useMemo<ChartData<'boxplot'>>(() => ({
        labels: ['array', '{boxplot values}', 'with items', 'as outliers'],
        datasets: [
            {
                label: 'Dataset 1',
                itemBorderColor: theme.palette.text.secondary,
                itemBackgroundColor: theme.palette.text.disabled,
                itemBorderWidth: 1,
                outlierRadius: 2,
                outlierBorderColor: theme.palette.text.secondary,
                outlierBackgroundColor: theme.palette.text.secondary,
                medianColor: theme.palette.tertiary.main,
                lowerBackgroundColor: theme.palette.tertiary.main,
                backgroundColor: `rgba(${theme.palette.secondary.mainChannel} / ${theme.palette.action.focusOpacity})`,
                borderColor: theme.palette.secondary.main,
                data: [
                    randomValues(100, 0, 100),
                    randomValues(100, 0, 20, [110]),
                    randomValues(100, 20, 70),
                    // empty data
                    [],
                ],
            },
            {
                label: 'Dataset 2',
                itemBorderColor: theme.palette.text.secondary,
                itemBackgroundColor: theme.palette.text.disabled,
                itemBorderWidth: 1,
                outlierRadius: 2,
                outlierBorderColor: theme.palette.text.secondary,
                outlierBackgroundColor: theme.palette.text.secondary,
                medianColor: theme.palette.tertiary.main,
                lowerBackgroundColor: theme.palette.tertiary.main,
                backgroundColor: `rgba(${theme.palette.primary.mainChannel} / ${theme.palette.action.focusOpacity})`,
                borderColor: theme.palette.primary.main,
                data: [
                    randomValues(100, 60, 100, [5, 10]),
                    randomValues(100, 0, 100),
                    randomValues(100, 0, 20),
                    randomValues(100, 20, 40),
                ],
            },
        ],
    }), [theme]);

    const options = React.useMemo<ChartOptions<'boxplot'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        elements: {
            boxandwhiskers: {
                itemRadius: 2,
                itemHitRadius: 4,
            },
        },
        plugins: {
            legend: {
                display: true,
            },
            tooltip: {
                enabled: true,
            },
            datalabels: {
                display: false,
            },
        },
    }), []);

    return (
        <>
            <BoxplotChart
                data={chartData}
                options={options}
                {...props}
            />
        </>
    );
}


const DemoViolinChart: React.FC<ChartInfoChartProps> = (props) => {
    const theme = useTheme();

    const chartData = React.useMemo<ChartData<'violin'>>(() => ({
        labels: ['array', '{violin values}', 'with items', 'as outliers'],
        datasets: [
            {
                label: 'Dataset 1',
                itemBorderColor: theme.palette.text.secondary,
                itemBackgroundColor: theme.palette.text.disabled,
                itemBorderWidth: 1,
                outlierRadius: 2,
                outlierBorderColor: theme.palette.text.secondary,
                outlierBackgroundColor: theme.palette.text.secondary,
                medianColor: theme.palette.secondary.main,
                lowerBackgroundColor: theme.palette.tertiary.main,
                backgroundColor: `rgba(${theme.palette.primary.mainChannel} / ${theme.palette.action.focusOpacity})`,
                borderColor: theme.palette.primary.main,
                data: [
                    [1, 2, 3, 4, 5, 11],
                    {
                        median: 4,
                        maxEstimate: 10,
                        coords: [
                            {v: 0, estimate: 0},
                            {v: 2, estimate: 10},
                            {v: 4, estimate: 6},
                            {v: 6, estimate: 7},
                            {v: 8, estimate: 0},
                        ],
                    },
                    {
                        median: 4,
                        maxEstimate: 10,
                        coords: [
                            {v: 0, estimate: 0},
                            {v: 2, estimate: 10},
                            {v: 4, estimate: 6},
                            {v: 6, estimate: 7},
                            {v: 8, estimate: 0},
                        ],
                        items: [1, 2, 3, 4, 5],
                    },
                    {
                        median: 4,
                        maxEstimate: 10,
                        coords: [
                            {v: 0, estimate: 0},
                            {v: 2, estimate: 10},
                            {v: 4, estimate: 6},
                            {v: 6, estimate: 7},
                            {v: 8, estimate: 0},
                        ],
                        outliers: [11],
                    },
                ],
            },
        ],
    }), [theme]);

    const options = React.useMemo<ChartOptions<'violin'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        elements: {
            violin: {
                itemRadius: 2,
                itemHitRadius: 4,
            },
        },
        plugins: {
            legend: {
                display: true,
            },
            tooltip: {
                enabled: true,
            },
            datalabels: {
                display: false,
            }
        }
    }), []);

    return (
        <>
            <ViolinChart
                data={chartData}
                options={options}
                {...props}
            />
        </>
    );
}

const ChoroplethMapCountries = [
    {
        label: 'Canada',
        topo: CanadaTopoJson as unknown as Topology,
        projection: 'mercator',
        color: 'blues',
        objectKey: 'can',
        provinceNames: {
            "bc": 'British Columbia', "ab": 'Alberta', "sk": 'Saskatchewan', "mb": 'Manitoba', "on": 'Ontario', "qc": 'Quebec', "nb": 'New Brunswick',
            "pe": 'Prince Edward Island', "ns": 'Nova Scotia', "nl": 'Newfoundland and Labrador', "yt": 'Yukon', "nt": 'Northwest Territories', "nu": 'Nunavut',
        },
        centroids: {
            bc: {lat: 53.7267, lng: -127.6476},
            ab: {lat: 53.9333, lng: -116.5765},
            sk: {lat: 52.9399, lng: -106.4509},
            mb: {lat: 53.7609, lng: -98.8139},
            on: {lat: 51.2538, lng: -85.3232},
            qc: {lat: 52.9399, lng: -73.5491},
            nb: {lat: 46.5653, lng: -66.4619},
            pe: {lat: 46.5107, lng: -63.4168},
            ns: {lat: 45.0000, lng: -63.0000},
            nl: {lat: 53.1355, lng: -57.6604},
            yt: {lat: 63.5000, lng: -135.0000},
            nt: {lat: 64.8255, lng: -124.8457},
            nu: {lat: 70.2998, lng: -83.1076},
        },
    },
    {
        label: 'USA',
        topo: USATopoJson as unknown as Topology,
        objectKey: 'usa',
        projection: 'albersUsa',
        color: 'greens',
        provinceNames: {
            "ca": 'California', "tx": 'Texas', "fl": 'Florida', "ny": 'New York', "il": 'Illinois', "pa": 'Pennsylvania', "oh": 'Ohio', "ga": 'Georgia',
            "nc": 'North Carolina', "mi": 'Michigan', "nj": "New Jersey", "va": "Virginia", "wa": "Washington", "az": "Arizona", "ma": "Massachusetts", "tn": "Tennessee",
            "in": "Indiana", "mo": "Missouri", "md": "Maryland", "wi": "Wisconsin", "co": "Colorado", "mn": "Minnesota", "sc": "South Carolina", "al": "Alabama", "la": "Louisiana",
            "ky": "Kentucky", "or": "Oregon", "ok": "Oklahoma", "ct": "Connecticut", "ia": "Iowa", "ut": "Utah", "nv": "Nevada", "ar": "Arkansas", "ms": "Mississippi",
            "ks": "Kansas", "nm": "New Mexico", "ne": "Nebraska", "wv": "West Virginia", "id": "Idaho", "hi": "Hawaii", "me": "Maine", "nh": "New Hampshire", "mt": "Montana",
            "ri": "Rhode Island", "de": "Delaware", "sd": "South Dakota", "nd": "North Dakota", "ak": "Alaska", "vt": "Vermont", "wy": "Wyoming", "dc": "District of Columbia",
            "pr": "Puerto Rico", "gu": "Guam", "vi": "U.S. Virgin Islands", "as": "American Samoa", "mp": "Northern Mariana Islands", "fm": "Federated States of Micronesia",
            "mh": "Marshall Islands", "pw": "Palau", "cnmi": "Commonwealth of the Northern Mariana Islands", "bk": "Baker Island", "hiat": "Howland Island", "ji": "Jarvis Island",
            "ja": "Johnston Atoll", "kr": "Kingman Reef", "mdw": "Midway Atoll", "wiat": "Wake Island",
        },
        centroids: {
            ca: {lat: 37.2500, lng: -119.7500},
            tx: {lat: 31.0000, lng: -99.0000},
            fl: {lat: 28.0000, lng: -82.0000},
            ny: {lat: 43.0000, lng: -75.0000},
            il: {lat: 40.0000, lng: -89.0000},
            pa: {lat: 41.0000, lng: -77.5000},
            oh: {lat: 40.4000, lng: -82.8000},
            ga: {lat: 32.7000, lng: -83.3000},
            nc: {lat: 35.5000, lng: -79.0000},
            mi: {lat: 44.3000, lng: -85.6000},
            nj: {lat: 40.1000, lng: -74.5000},
            va: {lat: 37.5000, lng: -78.7000},
            wa: {lat: 47.4000, lng: -120.6000},
            az: {lat: 34.3000, lng: -111.7000},
            ma: {lat: 42.4000, lng: -71.4000},
            tn: {lat: 35.8000, lng: -86.4000},
            in: {lat: 39.9000, lng: -86.3000},
            mo: {lat: 38.5000, lng: -92.5000},
            md: {lat: 39.0000, lng: -76.7000},
            wi: {lat: 44.5000, lng: -89.5000},
            co: {lat: 39.0000, lng: -105.5000},
            mn: {lat: 46.0000, lng: -94.0000},
            sc: {lat: 33.8000, lng: -81.2000},
            al: {lat: 32.8000, lng: -86.8000},
            la: {lat: 31.0000, lng: -92.0000},
            ky: {lat: 37.8000, lng: -85.8000},
            or: {lat: 44.0000, lng: -120.5000},
            ok: {lat: 35.6000, lng: -97.5000},
            ct: {lat: 41.6000, lng: -72.7000},
            ia: {lat: 42.0000, lng: -93.0000},
            ut: {lat: 39.3000, lng: -111.7000},
            nv: {lat: 39.5000, lng: -116.9000},
            ar: {lat: 35.0000, lng: -92.4000},
            ms: {lat: 32.7000, lng: -89.7000},
            ks: {lat: 38.5000, lng: -98.0000},
            nm: {lat: 34.5000, lng: -106.0000},
            ne: {lat: 41.5000, lng: -99.5000},
            wv: {lat: 38.6000, lng: -80.6000},
            id: {lat: 44.1000, lng: -114.7000},
            hi: {lat: 20.8000, lng: -156.3000},
            me: {lat: 45.2000, lng: -69.0000},
            nh: {lat: 43.7000, lng: -71.6000},
            mt: {lat: 46.9000, lng: -110.4000},
            ri: {lat: 41.7000, lng: -71.5000},
            de: {lat: 39.0000, lng: -75.5000},
            sd: {lat: 44.5000, lng: -100.3000},
            nd: {lat: 47.5000, lng: -100.5000},
            ak: {lat: 64.2000, lng: -149.5000},
            vt: {lat: 44.0000, lng: -72.7000},
            wy: {lat: 43.0000, lng: -107.5000},
            dc: {lat: 38.9072, lng: -77.0369},
            pr: {lat: 18.2000, lng: -66.5000},
            gu: {lat: 13.4400, lng: 144.7800},
            vi: {lat: 18.3400, lng: -64.9300},
            as: {lat: -14.2700, lng: -170.7000},
            mp: {lat: 15.2000, lng: 145.7500},     // Northern Mariana Islands
            fm: {lat: 6.8900, lng: 158.2500},      // Federated States of Micronesia (COFA)
            mh: {lat: 7.1300, lng: 171.1800},      // Marshall Islands (COFA)
            pw: {lat: 7.5000, lng: 134.5000},      // Palau (COFA)
            cnmi: {lat: 15.2000, lng: 145.7500},   // Duplicate key in your list; same as mp
            bk: {lat: 0.1930, lng: -176.4760},     // Baker Island
            hiat: {lat: 0.8060, lng: -176.6180},   // Howland Island
            ji: {lat: -0.3740, lng: -159.9960},    // Jarvis Island
            ja: {lat: 16.7290, lng: -169.5330},    // Johnston Atoll
            kr: {lat: 6.3830, lng: -162.4000},     // Kingman Reef
            mdw: {lat: 28.2070, lng: -177.3760},   // Midway Atoll
            wiat: {lat: 19.3000, lng: 166.6170},   // Wake Island (E longitude)
        }
    },
    {
        label: 'Mexico',
        topo: MexicoTopoJson as unknown as Topology,
        objectKey: 'mex',
        projection: 'mercator',
        color: 'ylOrRd',
        provinceNames: {
            "ags": 'Aguascalientes', "bc": 'Baja California', "bcs": 'Baja California Sur', "camp": 'Campeche', "chis": 'Chiapas', "chih": 'Chihuahua', "coah": 'Coahuila',
            "col": 'Colima', "dgo": 'Durango', "gto": 'Guanajuato', "gro": 'Guerrero', "hgo": 'Hidalgo', "jal": 'Jalisco', "mex": 'México', "mich": 'Michoacán',
            "mor": 'Morelos', "nay": 'Nayarit', "nl": 'Nuevo León', "oax": 'Oaxaca', "pue": 'Puebla', "qro": 'Querétaro', "qr": 'Quintana Roo', "slp": 'San Luis Potosí',
            "sin": 'Sinaloa', "son": 'Sonora', "tab": 'Tabasco', "tamps": 'Tamaulipas', "tlax": 'Tlaxcala', "ver": 'Veracruz', "yuc": 'Yucatán', "zac": 'Zacatecas',
        },
        centroids: {
            ags: {lat: 21.8800, lng: -102.3000},
            bc: {lat: 30.0000, lng: -115.0000},
            bcs: {lat: 26.0000, lng: -111.7000},
            camp: {lat: 19.3000, lng: -90.1000},
            chis: {lat: 16.4100, lng: -92.4100},
            chih: {lat: 28.6300, lng: -106.0700},
            coah: {lat: 27.3000, lng: -102.0000},
            col: {lat: 19.1200, lng: -104.0500},
            dgo: {lat: 24.0000, lng: -104.6700},
            gto: {lat: 20.9400, lng: -101.2600},
            gro: {lat: 17.5500, lng: -99.5000},
            hgo: {lat: 20.4800, lng: -98.9600},
            jal: {lat: 20.5000, lng: -103.7000},
            mex: {lat: 19.3000, lng: -99.6600},
            mich: {lat: 19.7000, lng: -101.2000},
            mor: {lat: 18.7700, lng: -99.1000},
            nay: {lat: 21.8000, lng: -104.8000},
            nl: {lat: 25.7000, lng: -100.3000},
            oax: {lat: 17.0700, lng: -96.7200},
            pue: {lat: 19.0000, lng: -97.9000},
            qro: {lat: 20.8000, lng: -99.9000},
            qr: {lat: 19.6000, lng: -88.0000},
            slp: {lat: 22.5000, lng: -100.5000},
            sin: {lat: 25.0000, lng: -107.5000},
            son: {lat: 29.3000, lng: -110.9000},
            tab: {lat: 17.8000, lng: -92.6000},
            tamps: {lat: 23.3000, lng: -98.9000},
            tlax: {lat: 19.4000, lng: -98.2000},
            ver: {lat: 19.3000, lng: -96.4000},
            yuc: {lat: 20.7000, lng: -89.0000},
            zac: {lat: 23.6000, lng: -102.6000},
        },
    }
]


const DemoChoroplethMapChart: React.FC<ChartInfoChartProps> = (props) => {
    const [shownCountry, setShownCountry] = React.useState<'can' | 'usa' | 'mex'>('usa');
    const theme = useTheme();

    const countryValues: Record<string, Record<string, number>> = React.useMemo(() => ({
        'can': {
            "bc": 0.4, "ab": 0.5, "sk": 0.6, "mb": 0.7, "on": 0.8, "qc": 0.9, "nb": 0.2, "pe": 0.1, "ns": 0.3, "nl": 0.4, "yt": 0.2, "nt": 0.3, "nu": 0.5,
        },
        'usa': {
            "ca": 0.9, "tx": 0.8, "fl": 0.7, "ny": 0.6, "il": 0.5, "pa": 0.4, "oh": 0.3, "ga": 0.2, "nc": 0.1, "mi": 0.2, "nj": 0.3, "va": 0.4, "wa": 0.5,
            "az": 0.6, "ma": 0.7, "tn": 0.8, "in": 0.9, "mo": 0.1, "md": 0.2, "wi": 0.3, "co": 0.4, "mn": 0.5, "sc": 0.6, "al": 0.7, "la": 0.8, "ky": 0.9,
            "or": 0.1, "ok": 0.2, "ct": 0.3, "ia": 0.4, "ut": 0.5, "nv": 0.6, "ar": 0.7, "ms": 0.8, "ks": 0.9, "nm": 0.1, "ne": 0.2, "wv": 0.3, "id": 0.4,
            "hi": 0.5, "me": 0.6, "nh": 0.7, "mt": 0.8, "ri": 0.9, "de": 0.1, "sd": 0.2, "nd": 0.3, "ak": 0.4, "vt": 0.5, "wy": 0.6, "dc": 0.7, "pr": 0.8,
            "gu": 0.9, "vi": 0.1, "as": 0.2, "mp": 0.3, "fm": 0.4, "mh": 0.5, "pw": 0.6, "cnmi": 0.7, "bk": 0.8, "hiat": 0.9, "ji": 0.1, "ja": 0.2, "kr": 0.3,
            "mdw": 0.4, "wiat": 0.5,
        },
        'mex': {
            "ags": 0.4, "bc": 0.5, "bcs": 0.6, "camp": 0.7, "chis": 0.8, "chih": 0.9, "coah": 0.2, "col": 0.1, "dgo": 0.3, "gto": 0.4, "gro": 0.5, "hgo": 0.6,
            "jal": 0.7, "mex": 0.8, "mich": 0.9, "mor": 0.1, "nay": 0.2, "nl": 0.3, "oax": 0.4, "pue": 0.5, "qro": 0.6, "qr": 0.7, "slp": 0.8, "sin": 0.9, "son": 0.1,
            "tab": 0.2, "tamps": 0.3, "tlax": 0.4, "ver": 0.5, "yuc": 0.6, "zac": 0.7,
        }
    }), []);


    const [title, labels, data, features, projection, color] = React.useMemo(() => {
        const country = ChoroplethMapCountries.find(c => c.objectKey === shownCountry);
        if (!country) {
            return ["", [], [], [], 'mercator', 'blues'];
        }
        const topo = country.topo;
        const provinces = topojson.feature<GeoJSON.GeoJsonProperties>(topo, topo.objects[shownCountry] as GeometryCollection) as unknown as GeoJSON.FeatureCollection;
        const labels = provinces.features.map((feature) => (feature.properties as any)?.name ?? '');
        const valuesForCountryByProvinceCode = countryValues[shownCountry];
        const provinceNameEntries = Object.entries(country.provinceNames);
        const data = provinces.features.map((feature) => {
            const provinceName = (feature.properties as any)?.name as string | undefined;
            const provinceCode = provinceNameEntries.find(([, name]) => name === provinceName)?.[0];
            if (!provinceCode) {
                return null;
            }
            const value = valuesForCountryByProvinceCode[provinceCode ?? ''] ?? 0;
            return {value, feature};
        }).filter(d => d !== null);
        return [
            country.label + ' Provinces / States',
            labels,
            data,
            provinces.features,
            country.projection,
            country.color,
        ];
    }, [shownCountry, countryValues]);

    const chartData = React.useMemo<ChartData<'choropleth'>>(() => ({
        labels: labels,
        datasets: [
            {
                label: title,
                showOutline: true,
                showGraticule: false,
                outline: features,
                data: data,
                outlineBorderWidth: 1,
                outlineBorderColor: theme.palette.text.primary,
            }
        ],
    }), [labels, title, features, data, theme.palette.text.primary]);

    const options = React.useMemo<ChartOptions<'choropleth'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                labels: {color: theme.palette.text.primary},
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: (ctx) => {
                        const raw = ctx.raw as any;
                        const name = raw?.feature?.properties?.name ?? ctx.label ?? '';
                        const val = raw?.value ?? ctx.parsed ?? '';
                        return `${name}: ${val}`;
                    },
                },
            },
            datalabels: {
                display: true,
                formatter: (value, context) => {
                    const raw = context.dataset.data[context.dataIndex] as any;
                    return raw?.feature?.properties?.name ?? context.chart.data.labels?.[context.dataIndex] ?? '';
                },
                color: theme.palette.text.primary,
                font: (context) => ({weight: 'bold', size: context.chart.width < 400 ? 8 : 12}),
                opacity: 0.7,
            },
        },
        scales: {
            projection: {
                axis: 'x',
                projection: projection,
            },
            color: {
                axis: 'x',
                interpolate: color as any,
                missing: theme.palette.action.disabledBackground,
            },
        },
    }), [projection, theme.palette.action.disabledBackground, theme.palette.text.primary, color]);

    return (
        <>
            <Box sx={{mb: 2, display: 'flex', gap: 2, flexDirection: 'column', maxHeight: 'inherit'}}>
                <Tabs variant='standard' value={shownCountry} onChange={(_, value) => setShownCountry(value)}>
                    <Tab label="Canada" value={'can'}/>
                    <Tab label="USA" value={'usa'}/>
                    <Tab label="Mexico" value={'mex'}/>
                </Tabs>
                <Box sx={{height: 'max-content', maxHeight: "calc(inherit - 48px - 16px)", overflow: 'hidden'}}>
                    <ChoroplethChart data={chartData} options={options}{...props}/>
                </Box>
            </Box>
        </>
    );
};


const DemoBubbleMapChart: React.FC<ChartInfoChartProps> = (props) => {
    const [shownCountry, setShownCountry] = React.useState<'can' | 'usa' | 'mex'>('usa');
    const theme = useTheme();

    const countryValues: Record<string, Record<string, number>> = React.useMemo(() => ({
        'can': {
            "bc": 0.4, "ab": 0.5, "sk": 0.6, "mb": 0.7, "on": 0.8, "qc": 0.9, "nb": 0.2, "pe": 0.1, "ns": 0.3, "nl": 0.4, "yt": 0.2, "nt": 0.3, "nu": 0.5,
        },
        'usa': {
            "ca": 0.9, "tx": 0.8, "fl": 0.7, "ny": 0.6, "il": 0.5, "pa": 0.4, "oh": 0.3, "ga": 0.2, "nc": 0.1, "mi": 0.2, "nj": 0.3, "va": 0.4, "wa": 0.5,
            "az": 0.6, "ma": 0.7, "tn": 0.8, "in": 0.9, "mo": 0.1, "md": 0.2, "wi": 0.3, "co": 0.4, "mn": 0.5, "sc": 0.6, "al": 0.7, "la": 0.8, "ky": 0.9,
            "or": 0.1, "ok": 0.2, "ct": 0.3, "ia": 0.4, "ut": 0.5, "nv": 0.6, "ar": 0.7, "ms": 0.8, "ks": 0.9, "nm": 0.1, "ne": 0.2, "wv": 0.3, "id": 0.4,
            "hi": 0.5, "me": 0.6, "nh": 0.7, "mt": 0.8, "ri": 0.9, "de": 0.1, "sd": 0.2, "nd": 0.3, "ak": 0.4, "vt": 0.5, "wy": 0.6, "dc": 0.7, "pr": 0.8,
            "gu": 0.9, "vi": 0.1, "as": 0.2, "mp": 0.3, "fm": 0.4, "mh": 0.5, "pw": 0.6, "cnmi": 0.7, "bk": 0.8, "hiat": 0.9, "ji": 0.1, "ja": 0.2, "kr": 0.3,
            "mdw": 0.4, "wiat": 0.5,
        },
        'mex': {
            "ags": 0.4, "bc": 0.5, "bcs": 0.6, "camp": 0.7, "chis": 0.8, "chih": 0.9, "coah": 0.2, "col": 0.1, "dgo": 0.3, "gto": 0.4, "gro": 0.5, "hgo": 0.6,
            "jal": 0.7, "mex": 0.8, "mich": 0.9, "mor": 0.1, "nay": 0.2, "nl": 0.3, "oax": 0.4, "pue": 0.5, "qro": 0.6, "qr": 0.7, "slp": 0.8, "sin": 0.9, "son": 0.1,
            "tab": 0.2, "tamps": 0.3, "tlax": 0.4, "ver": 0.5, "yuc": 0.6, "zac": 0.7,
        }
    }), []);


    const [title, labels, data, features, projection] = React.useMemo(() => {
        const country = ChoroplethMapCountries.find(c => c.objectKey === shownCountry);
        if (!country) {
            return ["", [], [], [], 'mercator', 'blues'];
        }
        const topo = country.topo;
        const provinces = topojson.feature<GeoJSON.GeoJsonProperties>(topo, topo.objects[shownCountry] as GeometryCollection) as unknown as GeoJSON.FeatureCollection;
        const labels = provinces.features.map((feature) => (feature.properties as any)?.name ?? '');
        const valuesForCountryByProvinceCode = countryValues[shownCountry];
        const provinceNameEntries = Object.entries(country.provinceNames);
        const data = provinces.features.map((feature) => {
            const provinceName = (feature.properties as any)?.name as string | undefined;
            const provinceCode = provinceNameEntries.find(([, name]) => name === provinceName)?.[0];
            if (!provinceCode) {
                return null;
            }
            const value = valuesForCountryByProvinceCode[provinceCode ?? ''] ?? 0;
            const centroid = country.centroids[provinceCode ?? ''];
            return {
                value,
                feature,
                latitude: centroid?.lat,
                longitude: centroid?.lng,
            };
        }).filter(e => !!e);
        return [
            country.label + ' Provinces / States',
            labels,
            data,
            provinces.features,
            country.projection,
            country.color,
        ];
    }, [shownCountry, countryValues]);

    const chartData = React.useMemo<ChartData<'bubbleMap'>>(() => ({
        labels: labels,
        datasets: [
            {
                label: title, showOutline: true, showGraticule: false, outline: features, data: data,
                outlineBorderColor: theme.palette.text.secondary,
                outlineBorderWidth: 1,
                backgroundColor: theme.palette.primary.main,
            }
        ],
    }), [labels, title, features, data, theme.palette.text.secondary, theme.palette.primary.main]);

    const options = React.useMemo<ChartOptions<'bubbleMap'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        borderColor: theme.palette.divider,
        plugins: {
            legend: {
                display: true,
                labels: {color: theme.palette.text.primary},
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: (ctx) => {
                        const raw = ctx.raw as any;
                        const name = raw?.feature?.properties?.name ?? ctx.label ?? '';
                        const val = raw?.value ?? ctx.parsed ?? '';
                        return `${name}: ${val}`;
                    },
                },
            },
            datalabels: {
                display: true,
                formatter: (value, context) => {
                    const raw = context.dataset.data[context.dataIndex] as any;
                    return raw?.feature?.properties?.name ?? context.chart.data.labels?.[context.dataIndex] ?? '';
                },
                color: theme.palette.text.primary,
                font: (context) => ({weight: 'bold', size: context.chart.width < 400 ? 8 : 12}),
                opacity: 0.7,
                align: 'top',
            },
        },
        scales: {
            projection: {
                axis: 'x',
                projection: projection,
            },
            size: {
                axis: 'x',
                size: [1, 20],
            },
        },
    }), [projection, theme.palette.divider, theme.palette.text.primary]);

    return (
        <>
            <Box sx={{mb: 2, display: 'flex', gap: 2, flexDirection: 'column', maxHeight: 'inherit'}}>
                <Tabs variant='standard' value={shownCountry} onChange={(_, value) => setShownCountry(value)}>
                    <Tab label="Canada" value={'can'}/>
                    <Tab label="USA" value={'usa'}/>
                    <Tab label="Mexico" value={'mex'}/>
                </Tabs>
                <Box sx={{height: 'max-content', maxHeight: "calc(inherit - 48px - 16px)", overflow: 'hidden'}}>
                    <BubbleMapChart
                        data={chartData}
                        options={options}
                        {...props}
                    />
                </Box>
            </Box>
        </>
    );
};


const DemoMatrixChart: React.FC<ChartInfoChartProps> = () => {
    const theme = useTheme();
    const cols = 30;
    const rows = 7;
    const color = theme.palette.info.mainChannel;
    const colsToRowsRatio = cols / rows;

    const data = React.useMemo(() => {
        const data = Array.from({length: cols * rows}, () => ({
            customer: "Customer " + Math.floor(Math.random() * 1000),
            qty: Math.floor(Math.random() * 100)
        }));
        const maxQty = Math.max(...data.map(d => d.qty));
        return data.map((d, i) => {
            const col = (i % cols) + 1;
            const row = Math.floor(i / cols) + 1;
            return {x: col, y: row, qty: d.qty, pct: d.qty / maxQty, customer: d.customer}
        });
    }, []);

    const chartData = React.useMemo<ChartData<'matrix'>>(() => {
        return ({
            labels: [
                ...Array.from({length: cols}, (_, i) => `Col ${i + 1}`),
            ],
            datasets: [
                {
                    label: 'Matrix Dataset',
                    data: data,
                    backgroundColor: ({raw}) => `rgba(${color} / ${Math.min(0.9, 0.1 + (raw as any).pct * 0.9)})`,
                    width: ({chart}) => Math.min(chart.width / cols, chart.height / rows) - (1 / colsToRowsRatio),
                    height: ({chart}) => Math.min(chart.width / cols, chart.height / rows) - (colsToRowsRatio),
                    borderWidth: 2,
                    hoverBorderWidth: 0,
                    borderColor: theme.palette.background.paper,
                }
            ],
        })
    }, [color, colsToRowsRatio, data, theme.palette.background.paper]);

    const options = React.useMemo<ChartOptions<'matrix'>>(() => ({
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: cols / rows,
        layout: {padding: 0, autoPadding: false},
        plugins: {
            legend: {display: false},
            datalabels: {
                display: true,
                formatter: (value) => (value as any).qty,
                color: theme.palette.getContrastText(theme.palette.primary.main),
                font: (context) => ({weight: 'bold', size: Math.min(16, Math.max(8, context.chart.width / cols / 3))}),
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    title: (context) => {
                        if (context.length === 0) return '';
                        const item = (context[0].dataset.data[context[0].dataIndex] as any);
                        return `Row: ${item.y}, Col: ${item.x}`;
                    },
                    label: (ctx) => {
                        const raw = ctx.raw as any;
                        return [
                            `Customer: ${raw.customer}`,
                            `Quantity: ${raw.qty}`,
                            `Percent of Max: ${(raw.pct * 100).toFixed(2)}%`
                        ]
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                offset: false,
                padding: 0,
                grid: {display: false},
                border: {display: false},
                min: 0.5,
                max: cols + 0.5,
                ticks: {
                    display: true,
                    maxTicksLimit: cols * 2,
                    includeBounds: false,
                    autoSkip: false,
                    align: 'center',
                    callback: (value, index) => index + 1,
                }
            },
            y: {
                display: true,
                offset: false,
                padding: 0,
                grid: {display: false},
                border: {display: false},
                min: 0.5,
                max: rows + 0.5,
                ticks: {
                    display: true,
                    maxTicksLimit: rows * 2,
                    includeBounds: false,
                    autoSkip: false,
                    align: 'center',
                    callback: (value, index) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][index] || '',
                }
            },
        },
    }), [theme.palette]);

    return (
        <>
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'inherit', width: 'inherit', maxHeight: 'inherit'}}>
                <Box sx={{width: '100%', height: 'auto', maxHeight: 'inherit', aspectRatio: `${cols} / ${rows}`, display: 'flex', justifyContent: 'center', alignItems: 'center',}}>
                    <MatrixChart
                        data={chartData}
                        options={options}
                    />
                </Box>
            </Box>
        </>
    );
}

const DemoPCPChart: React.FC<ChartInfoChartProps> = (props) => {
    const theme = useTheme();

    const colors = React.useMemo(() => ([
        '244, 235, 255',  // #f4ebff
        '225, 206, 254',  // #e1cefe
        '192, 162, 253',  // #c0a2fd
        '161, 107, 253',  // #a16bfd
        '253, 141, 60',  // #fd8d3c
        ' 241, 105, 19',  // #f16913
        '217, 72, 1',  // #d94801
        '166, 54, 3',  // #a63603
        '127, 39, 4',   // #7f2704
    ]), []);
    const colorFunc = useCallback((name, assigned) => {
        return assigned[name] || (assigned[name] = {
            color: `rgba(${colors[Object.keys(assigned).length % colors.length]}, 0.3)`,
            hoverColor: `rgba(${colors[Object.keys(assigned).length % colors.length]}, 1)`,
        });
    }, [colors]);

    const PCPAttrs = React.useMemo(() => (['mpg', 'hp', 'wt', 'qsec', 'gear', 'drat', 'disp', 'cyl', 'am']), []);
    const PCPData = React.useMemo(() => [
        {"model": "Mazda RX4", "mpg": 21, "cyl": 6, "disp": 160, "hp": 110, "drat": 3.9, "wt": 2.62, "qsec": 16.46, "vs": 0, "am": 1, "gear": 4, "carb": 4},
        {"model": "Mazda RX4 Wag", "mpg": 21, "cyl": 6, "disp": 160, "hp": 110, "drat": 3.9, "wt": 2.875, "qsec": 17.02, "vs": 0, "am": 1, "gear": 4, "carb": 4},
        {"model": "Datsun 710", "mpg": 22.8, "cyl": 4, "disp": 108, "hp": 93, "drat": 3.85, "wt": 2.32, "qsec": 18.61, "vs": 1, "am": 1, "gear": 4, "carb": 1},
        {"model": "Hornet 4 Drive", "mpg": 21.4, "cyl": 6, "disp": 258, "hp": 110, "drat": 3.08, "wt": 3.215, "qsec": 19.44, "vs": 1, "am": 0, "gear": 3, "carb": 1},
        {"model": "Hornet Sportabout", "mpg": 18.7, "cyl": 8, "disp": 360, "hp": 175, "drat": 3.15, "wt": 3.44, "qsec": 17.02, "vs": 0, "am": 0, "gear": 3, "carb": 2},
        {"model": "Valiant", "mpg": 18.1, "cyl": 6, "disp": 225, "hp": 105, "drat": 2.76, "wt": 3.46, "qsec": 20.22, "vs": 1, "am": 0, "gear": 3, "carb": 1},
        {"model": "Duster 360", "mpg": 14.3, "cyl": 8, "disp": 360, "hp": 245, "drat": 3.21, "wt": 3.57, "qsec": 15.84, "vs": 0, "am": 0, "gear": 3, "carb": 4},
        {"model": "Merc 240D", "mpg": 24.4, "cyl": 4, "disp": 146.7, "hp": 62, "drat": 3.69, "wt": 3.19, "qsec": 20, "vs": 1, "am": 0, "gear": 4, "carb": 2},
        {"model": "Merc 230", "mpg": 22.8, "cyl": 4, "disp": 140.8, "hp": 95, "drat": 3.92, "wt": 3.15, "qsec": 22.9, "vs": 1, "am": 0, "gear": 4, "carb": 2},
        {"model": "Merc 280", "mpg": 19.2, "cyl": 6, "disp": 167.6, "hp": 123, "drat": 3.92, "wt": 3.44, "qsec": 18.3, "vs": 1, "am": 0, "gear": 4, "carb": 4},
        {"model": "Merc 280C", "mpg": 17.8, "cyl": 6, "disp": 167.6, "hp": 123, "drat": 3.92, "wt": 3.44, "qsec": 18.9, "vs": 1, "am": 0, "gear": 4, "carb": 4},
        {"model": "Merc 450SE", "mpg": 16.4, "cyl": 8, "disp": 275.8, "hp": 180, "drat": 3.07, "wt": 4.07, "qsec": 17.4, "vs": 0, "am": 0, "gear": 3, "carb": 3},
        {"model": "Merc 450SL", "mpg": 17.3, "cyl": 8, "disp": 275.8, "hp": 180, "drat": 3.07, "wt": 3.73, "qsec": 17.6, "vs": 0, "am": 0, "gear": 3, "carb": 3},
        {"model": "Merc 450SLC", "mpg": 15.2, "cyl": 8, "disp": 275.8, "hp": 180, "drat": 3.07, "wt": 3.78, "qsec": 18, "vs": 0, "am": 0, "gear": 3, "carb": 3},
        {"model": "Cadillac Fleetwood", "mpg": 10.4, "cyl": 8, "disp": 472, "hp": 205, "drat": 2.93, "wt": 5.25, "qsec": 17.98, "vs": 0, "am": 0, "gear": 3, "carb": 4},
        {"model": "Lincoln Continental", "mpg": 10.4, "cyl": 8, "disp": 460, "hp": 215, "drat": 3, "wt": 5.424, "qsec": 17.82, "vs": 0, "am": 0, "gear": 3, "carb": 4},
        {"model": "Chrysler Imperial", "mpg": 14.7, "cyl": 8, "disp": 440, "hp": 230, "drat": 3.23, "wt": 5.345, "qsec": 17.42, "vs": 0, "am": 0, "gear": 3, "carb": 4},
        {"model": "Fiat 128", "mpg": 32.4, "cyl": 4, "disp": 78.7, "hp": 66, "drat": 4.08, "wt": 2.2, "qsec": 19.47, "vs": 1, "am": 1, "gear": 4, "carb": 1},
        {"model": "Honda Civic", "mpg": 30.4, "cyl": 4, "disp": 75.7, "hp": 52, "drat": 4.93, "wt": 1.615, "qsec": 18.52, "vs": 1, "am": 1, "gear": 4, "carb": 2},
        {"model": "Toyota Corolla", "mpg": 33.9, "cyl": 4, "disp": 71.1, "hp": 65, "drat": 4.22, "wt": 1.835, "qsec": 19.9, "vs": 1, "am": 1, "gear": 4, "carb": 1},
        {"model": "Toyota Corona", "mpg": 21.5, "cyl": 4, "disp": 120.1, "hp": 97, "drat": 3.7, "wt": 2.465, "qsec": 20.01, "vs": 1, "am": 0, "gear": 3, "carb": 1},
        {"model": "Dodge Challenger", "mpg": 15.5, "cyl": 8, "disp": 318, "hp": 150, "drat": 2.76, "wt": 3.52, "qsec": 16.87, "vs": 0, "am": 0, "gear": 3, "carb": 2},
        {"model": "AMC Javelin", "mpg": 15.2, "cyl": 8, "disp": 304, "hp": 150, "drat": 3.15, "wt": 3.435, "qsec": 17.3, "vs": 0, "am": 0, "gear": 3, "carb": 2},
        {"model": "Camaro Z28", "mpg": 13.3, "cyl": 8, "disp": 350, "hp": 245, "drat": 3.73, "wt": 3.84, "qsec": 15.41, "vs": 0, "am": 0, "gear": 3, "carb": 4},
        {"model": "Pontiac Firebird", "mpg": 19.2, "cyl": 8, "disp": 400, "hp": 175, "drat": 3.08, "wt": 3.845, "qsec": 17.05, "vs": 0, "am": 0, "gear": 3, "carb": 2},
        {"model": "Fiat X1-9", "mpg": 27.3, "cyl": 4, "disp": 79, "hp": 66, "drat": 4.08, "wt": 1.935, "qsec": 18.9, "vs": 1, "am": 1, "gear": 4, "carb": 1},
        {"model": "Porsche 914-2", "mpg": 26, "cyl": 4, "disp": 120.3, "hp": 91, "drat": 4.43, "wt": 2.14, "qsec": 16.7, "vs": 0, "am": 1, "gear": 5, "carb": 2},
        {"model": "Lotus Europa", "mpg": 30.4, "cyl": 4, "disp": 95.1, "hp": 113, "drat": 3.77, "wt": 1.513, "qsec": 16.9, "vs": 1, "am": 1, "gear": 5, "carb": 2},
        {"model": "Ford Pantera L", "mpg": 15.8, "cyl": 8, "disp": 351, "hp": 264, "drat": 4.22, "wt": 3.17, "qsec": 14.5, "vs": 0, "am": 1, "gear": 5, "carb": 4},
        {"model": "Ferrari Dino", "mpg": 19.7, "cyl": 6, "disp": 145, "hp": 175, "drat": 3.62, "wt": 2.77, "qsec": 15.5, "vs": 0, "am": 1, "gear": 5, "carb": 6},
        {"model": "Maserati Bora", "mpg": 15, "cyl": 8, "disp": 301, "hp": 335, "drat": 3.54, "wt": 3.57, "qsec": 14.6, "vs": 0, "am": 1, "gear": 5, "carb": 8},
        {"model": "Volvo 142E", "mpg": 21.4, "cyl": 4, "disp": 121, "hp": 109, "drat": 4.11, "wt": 2.78, "qsec": 18.6, "vs": 1, "am": 1, "gear": 4, "carb": 2}
    ], []);

    const PCPChartDemoData = React.useMemo<ChartData<'pcp'>>(() => {
        const assignedColors = {};
        return ({
            labels: PCPData.map((obj) => obj.model),
            datasets: PCPAttrs.map((attr) => ({
                label: attr,
                data: PCPData.map((obj) => obj[attr]),
                borderColor: () => colorFunc(attr, assignedColors).color,
                hoverBorderColor: () => colorFunc(attr, assignedColors).hoverColor,
            })),
        })
    }, [PCPAttrs, PCPData, colorFunc]);

    const options = React.useMemo<ChartOptions<'pcp'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        axisWidth: 20,
        elements: {
            lineSegment: {
                tension: 0.4,
            },
            logarithmicAxis: {
                backgroundColor: theme.palette.text.primary,
                color: theme.palette.text.primary,
            },
            linearAxis: {
                backgroundColor: theme.palette.text.primary,
                color: theme.palette.text.primary,
            },
        },
        plugins: {
            datalabels: {display: false},
            legend: {
                display: true,
                labels: {color: theme.palette.text.primary},
            },
            tooltip: {
                mode: 'point',
            },
        },
    }), [theme.palette]);


    return (
        <>
            <LinearParallelCoordinatesPlotChart
                data={PCPChartDemoData}
                options={options}
                {...props}
            />
        </>
    );
}

const DemoSankeyChart: React.FC<ChartInfoChartProps> = (props) => {
    const theme = useTheme();

    const colors = React.useMemo(() => (['255, 245, 235', '254, 230, 226', '253, 208, 162', '253, 174, 107', '253, 141, 60', '241, 105, 19', '217, 72, 1', '166, 54, 3', '127, 39, 4']), []);
    const colorFunc = useCallback((name, assigned) => {
        return assigned[name] || (assigned[name] = {
            color: `rgba(${colors[Object.keys(assigned).length % colors.length]}, 0.4)`,
            hoverColor: `rgba(${colors[Object.keys(assigned).length % colors.length]}, 1)`,
        });
    }, [colors]);

    const sankeyData = React.useMemo<ChartData<'sankey'>>(() => {
        const assignedColors = {};
        return ({
            datasets: [
                {
                    label: "Sankey Dataset",
                    color: theme.palette.getContrastText(theme.palette.background.paper),
                    data: [
                        {from: "Brazil", to: "Portugal", flow: 5},
                        {from: "Brazil", to: "France", flow: 1},
                        {from: "Brazil", to: "Spain", flow: 1},
                        {from: "Brazil", to: "England", flow: 1},
                        {from: "Canada", to: "Portugal", flow: 1},
                        {from: "Canada", to: "France", flow: 5},
                        {from: "Canada", to: "England", flow: 1},
                        {from: "Mexico", to: "Portugal", flow: 1},
                        {from: "Mexico", to: "France", flow: 1},
                        {from: "Mexico", to: "Spain", flow: 5},
                        {from: "Mexico", to: "England", flow: 1},
                        {from: "USA", to: "Portugal", flow: 1},
                        {from: "USA", to: "France", flow: 1},
                        {from: "USA", to: "Spain", flow: 1},
                        {from: "USA", to: "England", flow: 5},
                        {from: "Portugal", to: "Angola", flow: 2},
                        {from: "Portugal", to: "Senegal", flow: 1},
                        {from: "Portugal", to: "Morocco", flow: 1},
                        {from: "Portugal", to: "South Africa", flow: 3},
                        {from: "France", to: "Angola", flow: 1},
                        {from: "France", to: "Senegal", flow: 3},
                        {from: "France", to: "Mali", flow: 3},
                        {from: "France", to: "Morocco", flow: 3},
                        {from: "France", to: "South Africa", flow: 1},
                        {from: "Spain", to: "Senegal", flow: 1},
                        {from: "Spain", to: "Morocco", flow: 3},
                        {from: "Spain", to: "South Africa", flow: 1},
                        {from: "England", to: "Angola", flow: 1},
                        {from: "England", to: "Senegal", flow: 1},
                        {from: "England", to: "Morocco", flow: 2},
                        {from: "England", to: "South Africa", flow: 7},
                        {from: "South Africa", to: "China", flow: 5},
                        {from: "South Africa", to: "India", flow: 1},
                        {from: "South Africa", to: "Japan", flow: 3},
                        {from: "Angola", to: "China", flow: 5},
                        {from: "Angola", to: "India", flow: 1},
                        {from: "Angola", to: "Japan", flow: 3},
                        {from: "Senegal", to: "China", flow: 5},
                        {from: "Senegal", to: "India", flow: 1},
                        {from: "Senegal", to: "Japan", flow: 3},
                        {from: "Mali", to: "China", flow: 5},
                        {from: "Mali", to: "India", flow: 1},
                        {from: "Mali", to: "Japan", flow: 3},
                        {from: "Morocco", to: "China", flow: 5},
                        {from: "Morocco", to: "India", flow: 1},
                        {from: "Morocco", to: "Japan", flow: 3}
                    ],
                    colorFrom: (c) => colorFunc(c.dataset.data[c.dataIndex].from, assignedColors).color,
                    colorTo: (c) => colorFunc(c.dataset.data[c.dataIndex].to, assignedColors).color,
                    hoverColorTo: (c) => colorFunc(c.dataset.data[c.dataIndex].to, assignedColors).hoverColor,
                    hoverColorFrom: (c) => colorFunc(c.dataset.data[c.dataIndex].from, assignedColors).hoverColor,
                    colorMode: "gradient",
                    borderWidth: 0
                }
            ],
        });
    }, [colorFunc, theme.palette]);

    const options = React.useMemo<ChartOptions<'sankey'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: true, labels: {color: theme.palette.text.primary},},
            tooltip: {
                enabled: true,
                mode: 'nearest',
                intersect: false,
            },
            datalabels: {display: false}
        },
    }), [theme.palette]);

    return (
        <>
            <SankeyChart
                data={sankeyData}
                options={options}
                {...props}
            />
        </>
    );
}

const DemoVennDiagramChart: React.FC<ChartInfoChartProps> = (props) => {
    const theme = useTheme();

    const chartData = React.useMemo<ChartData<'venn'>>(() => ({
        labels: ['A', 'B', 'C'],
        datasets: [],
    }), []);

    const options = React.useMemo<ChartOptions<'venn'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {display: true, labels: {color: theme.palette.text.primary},},
            tooltip: {
                enabled: true,
                mode: 'nearest',
                intersect: false,
            },
            datalabels: {display: false}
        },
    }), [theme.palette]);

    return (
        <>

        </>
    );
}